import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "./Supabase";

import Navbar from "./components/Navbar";
import InvoiceList from "./pages/InvoicesPage";
import AuthScreen from "./pages/AuthScreen";
import InvoiceGeneratorPage from "./pages/InvoiceGeneratorPage";
import MetricsPage from "./pages/MetricsPage";
import NotFoundPage from "./pages/NotFoundPage";
import ProfilePage from "./pages/ProfilePage";
import RequestFeaturePage from "./pages/RequestFeaturePage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import SubscriptionConfirmed from "./pages/SubscriptionConfirmed";
import FirstTimeTermsPage from "./pages/FirstTimeTermsPage";
import { PLAN_LIMITS } from "./lib/planLimits";
import { useNotifications } from "./components/NotificationCenter";
import { clampFields, INPUT_LIMITS } from "./lib/inputLimits";
import { PRIVACY_URL, TERMS_URL, TERMS_VERSION } from "./lib/legal";

const FREE_PLAN = {
  planId: PLAN_LIMITS.free.id,
  ...PLAN_LIMITS.free,
};

const TAB_PATHS = {
  invoices: "/",
  metrics: "/metrics",
  profile: "/profile",
  subscriptions: "/subscriptions",
  "request-feature": "/feature-request",
};

const deriveTabFromPath = (pathname = "/") => {
  const normalizedPath = pathname || "/";

  if (normalizedPath === "/" || normalizedPath.startsWith("/invoices")) {
    return "invoices";
  }
  if (normalizedPath.startsWith("/feature-request")) return "request-feature";
  if (normalizedPath.startsWith("/subscriptions")) return "subscriptions";
  if (normalizedPath.startsWith("/metrics")) return "metrics";
  if (normalizedPath.startsWith("/profile")) return "profile";
  return "not-found";
};

const App = () => {
  const { notify } = useNotifications();
  const [activeTab, setActiveTab] = useState(() =>
    deriveTabFromPath(
      typeof window !== "undefined" ? window.location.pathname : "/"
    )
  );
  const [isSubscriptionConfirmedRoute, setIsSubscriptionConfirmedRoute] =
    useState(
      typeof window !== "undefined" &&
        window.location.pathname.startsWith("/subscriptions/confirmed")
    );
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [checkingProfile, setCheckingProfile] = useState(false);
  const [needsTermsAcceptance, setNeedsTermsAcceptance] = useState(false);
  const [acceptingTerms, setAcceptingTerms] = useState(false);
  const [profileCheckError, setProfileCheckError] = useState(null);
  const [profileCheckNonce, setProfileCheckNonce] = useState(0);

  const [showInvoiceEditor, setShowInvoiceEditor] = useState(false);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [invoiceUsage, setInvoiceUsage] = useState(0);
  const [planLimits, setPlanLimits] = useState(FREE_PLAN);

  const userId = user?.id ?? null;
  const closeInvoiceEditor = useCallback(() => {
    setShowInvoiceEditor(false);
    setEditingInvoiceId(null);
  }, []);

  const changeTab = useCallback(
    (tab, { preserveSearch = false } = {}) => {
      setActiveTab(tab);

      if (typeof window !== "undefined") {
        const basePath = TAB_PATHS[tab] || "/";
        const search = preserveSearch ? window.location.search : "";
        const currentUrl = `${window.location.pathname}${window.location.search}`;
        const nextUrl = `${basePath}${search}`;
        if (currentUrl !== nextUrl) {
          window.history.pushState({}, "", nextUrl);
        }
        setIsSubscriptionConfirmedRoute(
          basePath.startsWith("/subscriptions/confirmed")
        );
      }

      if (tab !== "invoices") {
        closeInvoiceEditor();
      }
    },
    [closeInvoiceEditor]
  );

  const syncTabToLocation = useCallback(() => {
    if (typeof window === "undefined") return;
    const { pathname } = window.location;
    const nextTab = deriveTabFromPath(pathname);
    setActiveTab(nextTab);
    setIsSubscriptionConfirmedRoute(
      pathname.startsWith("/subscriptions/confirmed")
    );
    if (nextTab !== "invoices") {
      closeInvoiceEditor();
    }
  }, [closeInvoiceEditor]);

  const retryProfileCheck = useCallback(() => {
    setProfileCheckError(null);
    setProfileCheckNonce((n) => n + 1);
  }, []);

  const loadPlanViaProfiles = useCallback(async () => {
    if (!userId) {
      return { plan: FREE_PLAN, invoiceUsage: 0 };
    }

    const { data, error } = await supabase
      .from("profiles")
      .select(
        "plan, invoice_usage, subscription_status, plan_expires_at, plan_interval"
      )
      .eq("id", userId)
      .maybeSingle();

    if (error) throw error;

    const planKey = data?.plan === "pro" ? "pro" : "free";
    const fallback = PLAN_LIMITS[planKey] ?? PLAN_LIMITS.free;
    const plan = {
      planId: planKey,
      ...fallback,
    };

    const invoiceUsage = Number(data?.invoice_usage ?? 0);

    return { plan, invoiceUsage };
  }, [userId]);

  const refreshPlanSnapshot = useCallback(async () => {
    if (!userId) {
      setPlanLimits(FREE_PLAN);
      setInvoiceUsage(0);
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke(
        "get-plan-limits"
      );
      if (error) throw error;

      if (data?.plan && data.plan.planId) {
        const fallback = PLAN_LIMITS[data.plan.planId] ?? PLAN_LIMITS.free;
        const normalized = {
          planId: data.plan.planId,
          ...fallback,
          ...data.plan,
        };

        [
          "invoiceLimit",
          "maxQuickSelectCustomers",
          "maxQuickSelectJobs",
          "editableFieldsBeforeNewInvoice",
        ].forEach((key) => {
          if (normalized[key] == null) {
            normalized[key] = Infinity;
          }
        });

        setPlanLimits(normalized);
      } else {
        setPlanLimits(FREE_PLAN);
      }
      if (typeof data?.invoiceUsage === "number") {
        setInvoiceUsage(data.invoiceUsage);
      } else {
        setInvoiceUsage(0);
      }
    } catch (e) {
      console.warn(
        "refreshPlanSnapshot failed via Edge Function, attempting fallback",
        e
      );
      try {
        const fallback = await loadPlanViaProfiles();
        setPlanLimits(fallback.plan);
        setInvoiceUsage(fallback.invoiceUsage);
      } catch (fallbackError) {
        console.warn("Fallback plan fetch failed", fallbackError);
        setPlanLimits(FREE_PLAN);
        setInvoiceUsage(0);
      }
    }
  }, [userId, loadPlanViaProfiles]);

  const refreshInvoiceUsage = useCallback(async () => {
    await refreshPlanSnapshot();
  }, [refreshPlanSnapshot]);

  const handleAcceptTerms = useCallback(async () => {
    if (!userId) {
      throw new Error("We could not find your account. Please sign in again.");
    }

    const resolvedEmail = (
      user?.email ||
      user?.user_metadata?.email ||
      ""
    ).trim();

    if (!resolvedEmail) {
      throw new Error(
        "We could not read an email for your account. Please add an email to continue."
      );
    }

    const termsAcceptedAt = new Date().toISOString();

    try {
      setAcceptingTerms(true);
      setProfileCheckError(null);

      const profilePayload = clampFields(
        {
          id: userId,
          email: resolvedEmail,
          terms_accepted: true,
          terms_accepted_at: termsAcceptedAt,
          terms_version: TERMS_VERSION,
        },
        {
          email: INPUT_LIMITS.email,
          terms_version: INPUT_LIMITS.termsVersion,
        }
      );

      const { error } = await supabase
        .from("profiles")
        .upsert(profilePayload, { onConflict: "id" });

      if (error) throw error;

      setNeedsTermsAcceptance(false);
      setProfileCheckNonce((n) => n + 1);
      await refreshPlanSnapshot();
    } catch (e) {
      console.warn("Saving terms acceptance failed", e);
      setProfileCheckError(
        e?.message || "Could not save your acceptance. Please try again."
      );
      throw e;
    } finally {
      setAcceptingTerms(false);
    }
  }, [userId, user, refreshPlanSnapshot]);

  const handleSignOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.warn("Sign-out failed", e);
    } finally {
      setUser(null);
      setNeedsTermsAcceptance(false);
      setProfileCheckError(null);
      setCheckingProfile(false);
    }
  }, []);

  useEffect(() => {
    refreshPlanSnapshot();
  }, [refreshPlanSnapshot]);

  useEffect(() => {
    syncTabToLocation();
    window.addEventListener("popstate", syncTabToLocation);
    return () => window.removeEventListener("popstate", syncTabToLocation);
  }, [syncTabToLocation]);

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      try {
        const { data, error } = await supabase.auth.getUser();
        if (!isMounted) return;
        if (error) {
          console.warn("getUser error", error);
          setUser(null);
        } else {
          setUser(data?.user ?? null);
        }
      } catch (e) {
        console.warn("getUser failed", e);
        if (isMounted) setUser(null);
      } finally {
        if (isMounted) setAuthLoading(false);
      }
    };

    init();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    let isCurrent = true;

    const checkProfileTerms = async () => {
      if (!userId) {
        if (isCurrent) {
          setNeedsTermsAcceptance(false);
          setProfileCheckError(null);
          setCheckingProfile(false);
        }
        return;
      }

      if (isCurrent) {
        setCheckingProfile(true);
        setProfileCheckError(null);
      }

      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("terms_accepted, terms_version, terms_accepted_at")
          .eq("id", userId)
          .maybeSingle();

        if (!isCurrent) return;
        if (error) throw error;

        setNeedsTermsAcceptance(!data?.terms_accepted);
      } catch (e) {
        if (!isCurrent) return;
        console.warn("Profile check failed", e);
        setProfileCheckError(
          e?.message || "We could not confirm your terms acceptance."
        );
        setNeedsTermsAcceptance(true);
      } finally {
        if (isCurrent) setCheckingProfile(false);
      }
    };

    checkProfileTerms();

    return () => {
      isCurrent = false;
    };
  }, [userId, profileCheckNonce]);

  const isTermsRoute =
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/terms");

  const isPrivacyRoute =
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/privacy");

  const isResetPasswordRoute =
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/reset-password");

  const isNotFoundRoute = activeTab === "not-found";

  if (isTermsRoute) {
    return <TermsPage />;
  }

  if (isPrivacyRoute) {
    return <PrivacyPage />;
  }

  if (isResetPasswordRoute) {
    return <ResetPasswordPage />;
  }

  if (isNotFoundRoute) {
    return <NotFoundPage onBack={() => changeTab("invoices")} />;
  }

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-5 shadow-sm text-sm text-slate-600">
          Checking your session…
        </div>
      </div>
    );
  }

  if (!user) {
    return <AuthScreen />;
  }

  if (checkingProfile && !needsTermsAcceptance) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-5 shadow-sm text-sm text-slate-600">
          Preparing your account…
        </div>
      </div>
    );
  }

  if (needsTermsAcceptance) {
    return (
      <FirstTimeTermsPage
        user={user}
        loading={acceptingTerms || checkingProfile}
        error={profileCheckError}
        onAccept={handleAcceptTerms}
        onRetry={retryProfileCheck}
        onSignOut={handleSignOut}
      />
    );
  }

  const subscriptionConfirmationStatus =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("status")
      : null;
  const isSubscriptionConfirmed =
    subscriptionConfirmationStatus === null ||
    subscriptionConfirmationStatus === "success";

  const openNewInvoice = () => {
    setEditingInvoiceId(null);
    setShowInvoiceEditor(true);
  };

  const openEditInvoice = ({ id }) => {
    setEditingInvoiceId(id);
    setShowInvoiceEditor(true);
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <Navbar
        activeTab={activeTab}
        planLimits={planLimits}
        onTabChange={(tab) => {
          if (tab === "metrics" && !planLimits.canAccessMetrics) {
            notify({
              type: "warning",
              title: "Pro plan required",
              message:
                "Upgrade to Pro to unlock revenue metrics and performance trends.",
            });
            changeTab("subscriptions");
            return;
          }

          changeTab(tab);
        }}
      />

      <main className="mx-auto flex w-full max-w-6xl flex-1 flex-col px-4 py-6 md:py-10">
        {isSubscriptionConfirmedRoute ? (
          <SubscriptionConfirmed isConfirmed={isSubscriptionConfirmed} />
        ) : (
          <>
            {activeTab === "invoices" &&
              (showInvoiceEditor ? (
                <InvoiceGeneratorPage
                  invoiceId={editingInvoiceId}
                  invoiceUsage={invoiceUsage}
                  planLimits={planLimits}
                  onBack={closeInvoiceEditor}
                  onUpgrade={() => changeTab("subscriptions")}
                  refreshInvoiceUsage={refreshInvoiceUsage}
                  onSaved={closeInvoiceEditor}
                />
              ) : (
                <InvoiceList
                  onCreate={openNewInvoice}
                  onEdit={openEditInvoice}
                  onUpgrade={() => changeTab("subscriptions")}
                  planLimits={planLimits}
                  invoiceUsage={invoiceUsage}
                />
              ))}

            {activeTab === "metrics" && planLimits.canAccessMetrics && (
              <MetricsPage />
            )}
            {activeTab === "profile" && (
              <ProfilePage
                user={user}
                onManageSubscription={() => changeTab("subscriptions")}
                onRequestFeature={() => changeTab("request-feature")}
              />
            )}

            {activeTab === "subscriptions" && (
              <SubscriptionPage onPlanUpdated={refreshPlanSnapshot} />
            )}

            {activeTab === "request-feature" && (
              <RequestFeaturePage
                user={user}
                onBack={() => changeTab("profile")}
              />
            )}
          </>
        )}
      </main>

      <footer className="border-t bg-white px-4 py-4 text-xs text-slate-500">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-2 text-center sm:flex-row sm:text-left">
          <div>© {new Date().getFullYear()} InvoiceU · All rights reserved</div>
          <div className="flex items-center gap-3">
            <a
              href={TERMS_URL}
              className="font-semibold text-slate-700 underline underline-offset-2 hover:text-slate-900"
            >
              Terms &amp; Conditions
            </a>
            <span aria-hidden="true" className="text-slate-300">
              •
            </span>
            <a
              href={PRIVACY_URL}
              className="font-semibold text-slate-700 underline underline-offset-2 hover:text-slate-900"
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
